import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, App, Button, Dropdown, Flex, Tag, Typography, type MenuProps, type TableProps } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { MEMBER_ACCOUNT_STATUS, MemberEditorModal, type MemberEditorRecord } from '@/features/access/index.ts'
import { usePageTableNavigation, type DateRangeFormValue, type PageTableSearch } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  getCreatedAtSearch,
  getCreatedAtSortOrder,
  getDateRangeFormValue,
  getTableSortOrder
} from '@/utils/list-query.ts'

dayjs.extend(utc)

type MemberType = 'employee' | 'agent'

type MemberRow = MemberEditorRecord & {
  isDisable: boolean
  account: {
    email: string
    isSuperAdmin?: boolean
    status: 'pendingApproval' | 'active' | 'rejected' | 'suspended'
  } | null
  createdAt: Date
}

type MemberQuery = PageTableSearch & {
  id?: number
  keyword?: string
  createdFrom?: string
  createdTo?: string
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
  type?: MemberType
  isDisable?: boolean
  isSalesperson?: boolean
}

type MemberSearch = Pick<MemberQuery, 'id' | 'keyword' | 'type'> &
  PageTableSearch & {
    createdAtRange?: DateRangeFormValue
    memberStatus?: 'active' | 'disabled'
    salespersonStatus?: 'enabled' | 'disabled'
    createdFrom?: string
    createdTo?: string
    sortField?: 'createdAt'
    sortOrder?: 'asc' | 'desc'
  }

const memberSearchSchema = z.object({
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(255).optional().catch(undefined),
  ...createdAtSearchShape,
  ...createdAtSortSearchShape,
  type: z.enum(['employee', 'agent']).optional().catch(undefined),
  memberStatus: z.enum(['active', 'disabled']).optional().catch(undefined),
  salespersonStatus: z.enum(['enabled', 'disabled']).optional().catch(undefined)
})

const listQuery = createListQueryPersistence('super-mega:access:members:list-query', memberSearchSchema)

export const Route = createFileRoute('/_authenticated/access/members')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: MembersPage,
  head: () => ({ meta: [{ title: '成员管理 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function createColumns({
  canManageAgents,
  canManageEmployees,
  onEdit,
  onConfirmSetDisabled,
  onConfirmDelete,
  isSuperAdmin,
  currentMemberId,
  sortOrder
}: {
  canManageAgents: boolean
  canManageEmployees: boolean
  onEdit: (memberId: number) => void
  onConfirmSetDisabled: (member: MemberRow) => void
  onConfirmDelete: (member: MemberRow) => void
  isSuperAdmin: boolean
  currentMemberId: number
  sortOrder?: 'asc' | 'desc'
}): ProColumns<MemberRow>[] {
  return [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '姓名、邮箱、手机或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'id',
      copyable: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      render: (_, record) => (
        <Flex className="w-full min-w-0" vertical>
          <Typography.Text className="block max-w-full truncate" strong>
            {record.name}
          </Typography.Text>
          <Typography.Text className="block max-w-full truncate" type="secondary">
            {record.type === 'employee' ? record.jobTitle || '未填写岗位' : '代理商'}
          </Typography.Text>
        </Flex>
      ),
      search: false,
      title: '成员',
      width: '16rem'
    },
    {
      dataIndex: 'type',
      fieldProps: { allowClear: true, placeholder: '全部成员类型…' },
      render: (_, record) => (
        <Tag color={record.type === 'agent' ? 'cyan' : 'blue'}>{record.type === 'agent' ? '代理商' : '内部员工'}</Tag>
      ),
      title: '类型',
      valueEnum: {
        employee: { text: '内部员工' },
        agent: { text: '代理商' }
      },
      width: '7.5rem'
    },
    {
      dataIndex: 'contact',
      render: (_, record) => (
        <Flex className="w-full min-w-0" vertical>
          <Typography.Text className="block max-w-full truncate" copyable>
            {record.email || '未填写邮箱'}
          </Typography.Text>
          <Typography.Text className="block max-w-full truncate" type="secondary">
            {record.phone || '未填写手机'}
          </Typography.Text>
        </Flex>
      ),
      search: false,
      title: '联系方式',
      width: '14rem'
    },
    {
      dataIndex: 'account',
      render: (_, record) => {
        if (!record.account) {
          return <Tag>未绑定账号</Tag>
        }
        const status = MEMBER_ACCOUNT_STATUS[record.account.status]
        return (
          <Flex align="start" gap="small" vertical>
            <Typography.Text copyable ellipsis>
              {record.account.email}
            </Typography.Text>
            <Tag color={status.color}>{status.text}</Tag>
          </Flex>
        )
      },
      search: false,
      title: '账号',
      width: '14rem'
    },
    {
      dataIndex: 'hireDate',
      render: (_, record) => (record.hireDate ? dayjs.utc(record.hireDate).format('YYYY-MM-DD') : '—'),
      search: false,
      title: '入职时间',
      width: '10rem'
    },
    {
      dataIndex: 'isSalesperson',
      render: (_, record) => (
        <Tag color={record.isSalesperson ? 'success' : 'default'}>{record.isSalesperson ? '是' : '否'}</Tag>
      ),
      search: false,
      title: '业务员',
      width: '7rem'
    },
    {
      dataIndex: 'memberStatus',
      fieldProps: { allowClear: true, placeholder: '全部成员状态…' },
      hideInTable: true,
      title: '状态',
      valueEnum: {
        active: { text: '在职/合作中' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'salespersonStatus',
      fieldProps: { allowClear: true, placeholder: '全部业务员状态…' },
      hideInTable: true,
      title: '业务员状态',
      valueEnum: {
        enabled: { text: '已启用' },
        disabled: { text: '未启用' }
      }
    },
    {
      dataIndex: 'isDisable',
      render: (_, record) => (
        <Tag color={record.isDisable ? 'default' : 'success'}>{record.isDisable ? '已停用' : '正常'}</Tag>
      ),
      search: false,
      title: '成员状态',
      width: '7.5rem'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: getTableSortOrder(sortOrder),
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        if (record.account?.isSuperAdmin && !isSuperAdmin) return '受保护成员'
        const canManage = record.type === 'agent' ? canManageAgents : canManageEmployees
        if (!canManage) {
          return '—'
        }
        const actions: TableRowAction[] = [
          { key: 'edit', label: '编辑', onClick: () => onEdit(record.id) },
          {
            key: 'status',
            label: record.isDisable ? '恢复' : '停用',
            danger: !record.isDisable,
            onClick: () => onConfirmSetDisabled(record)
          }
        ]
        if (isSuperAdmin) {
          actions.push({
            key: 'delete',
            permissions: ['member.delete'],
            label: !record.isDisable
              ? '删除成员（请先停用）'
              : record.account
                ? '删除成员（绑定账号不可删除）'
                : '删除成员',
            danger: true,
            disabled: !record.isDisable || Boolean(record.account) || record.id === currentMemberId,
            onClick: () => onConfirmDelete(record)
          })
        }
        return <TableRowActions resource={`member.${record.type}`} actions={actions} />
      },
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]
}

function MembersPage() {
  const { currentSession: initialSession } = Route.useRouteContext()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const navigate = Route.useNavigate()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: initialSession.id,
    navigate
  })
  const [editing, setEditing] = useState<number | 'new'>()
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([])
  const sessionSnapshot = useQuery({ ...trpc.auth.session.queryOptions(), enabled: false })
  const currentSession =
    sessionSnapshot.data?.status === 'active' && sessionSnapshot.data.member
      ? { ...sessionSnapshot.data, member: sessionSnapshot.data.member }
      : initialSession
  let memberDisabledFilter: boolean | undefined
  if (search.memberStatus === 'active') {
    memberDisabledFilter = false
  } else if (search.memberStatus === 'disabled') {
    memberDisabledFilter = true
  }
  let salespersonFilter: boolean | undefined
  if (search.salespersonStatus === 'enabled') {
    salespersonFilter = true
  } else if (search.salespersonStatus === 'disabled') {
    salespersonFilter = false
  }
  const members = useQuery({
    ...trpc.member.list.queryOptions({
      current: search.current,
      id: search.id,
      isDisable: memberDisabledFilter,
      isSalesperson: salespersonFilter,
      keyword: search.keyword,
      pageSize: search.pageSize,
      type: search.type,
      createdFrom: search.createdFrom,
      createdTo: search.createdTo,
      sortField: search.sortField,
      sortOrder: search.sortOrder
    }),
    placeholderData: keepPreviousData
  })
  const batchDeleteMembers = useMutation(trpc.member.batchDelete.mutationOptions())
  const deleteMember = useMutation(trpc.member.delete.mutationOptions())
  const setDisabled = useMutation(trpc.member.setDisabled.mutationOptions())
  const batchSetDisabled = useMutation(trpc.member.batchSetDisabled.mutationOptions())
  const canManageEmployees =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('member.employee.') && !permission.endsWith('.read')
    )
  const canManageAgents =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('member.agent.') && !permission.endsWith('.read')
    )

  const closeEditor = () => setEditing(undefined)
  const invalidateMembers = async (includeAccounts = false) => {
    const invalidations = [
      queryClient.invalidateQueries(trpc.member.list.queryFilter()),
      queryClient.invalidateQueries(trpc.member.tags.queryFilter()),
      queryClient.invalidateQueries(trpc.accountApplication.unboundEmployeeOptions.queryFilter())
    ]
    if (includeAccounts) {
      invalidations.push(queryClient.invalidateQueries(trpc.account.list.queryFilter()))
    }
    await Promise.all(invalidations)
  }
  const table = usePageTableNavigation<MemberSearch>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (currentSearch) => ({
      createdAtRange: getDateRangeFormValue(currentSearch.createdFrom, currentSearch.createdTo),
      keyword: currentSearch.keyword ?? (currentSearch.id === undefined ? undefined : String(currentSearch.id)),
      memberStatus: currentSearch.memberStatus,
      salespersonStatus: currentSearch.salespersonStatus,
      type: currentSearch.type
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      memberStatus: values.memberStatus,
      salespersonStatus: values.salespersonStatus,
      type: values.type
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      closeEditor()
      setSelectedMemberIds([])
    },
    search,
    showTotal: (total) => `共 ${total} 位成员`
  })

  const handleTableChange: NonNullable<TableProps<MemberRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      closeEditor()
      setSelectedMemberIds([])
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const changeMemberStatus = async (member: MemberRow) => {
    try {
      await setDisabled.mutateAsync({ id: member.id, isDisabled: !member.isDisable })
      void message.success(member.isDisable ? '成员已恢复' : '成员已停用')
      await invalidateMembers(true)
    } catch (error) {
      void message.error(getErrorMessage(error, '成员状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isDisabled: boolean) => {
    try {
      const result = await batchSetDisabled.mutateAsync({ ids: selectedMemberIds, isDisabled })
      setSelectedMemberIds([])
      void message.success(`已${isDisabled ? '停用' : '恢复'} ${result.count} 个成员`)
      await invalidateMembers(true)
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新成员状态失败'))
    }
  }

  const confirmChangeSelectedStatus = (isDisabled: boolean) => {
    if (!isDisabled) {
      void changeSelectedStatus(false)
      return
    }
    modal.confirm({
      content: '成员账号也会同时停用。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(true),
      title: `停用已选 ${selectedMemberIds.length} 个成员？`
    })
  }

  const confirmDeleteMembers = (ids: number[], member?: MemberRow) => {
    const targetIds = [...ids]
    const targetIdSet = new Set(targetIds)
    modal.confirm({
      title: member ? `永久删除成员 ${member.name}（ID：${member.id}）？` : `永久删除已选 ${targetIds.length} 位成员？`,
      content:
        '成员档案将永久删除，无法恢复。系统会逐条尝试删除，符合条件的成员会成功删除，不符合条件的成员会计为失败。',
      focusable: { autoFocusButton: 'cancel' },
      okButtonProps: { danger: true },
      okText: member ? '永久删除' : '确认批量删除',
      cancelText: '取消',
      onOk: async () => {
        let successCount = 0
        let failedCount = 0
        try {
          if (member) {
            await deleteMember.mutateAsync(member.id)
            successCount = 1
          } else {
            const result = await batchDeleteMembers.mutateAsync({ ids: targetIds })
            successCount = result.successCount
            failedCount = result.failedCount
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '删除成员失败'))
          throw error
        }
        setSelectedMemberIds([])
        closeEditor()
        if (member) {
          void message.success('成员删除成功')
        } else {
          const resultMessage = `删除完成：成功 ${successCount} 个，失败 ${failedCount} 个`
          if (failedCount === 0) {
            void message.success(resultMessage)
          } else if (successCount === 0) {
            void message.error(resultMessage)
          } else {
            void message.warning(resultMessage)
          }
        }
        await Promise.all([
          invalidateMembers(),
          queryClient.invalidateQueries(trpc.member.detail.queryFilter()),
          queryClient.invalidateQueries(trpc.member.creatorOptions.queryFilter())
        ])
        if (
          failedCount === 0 &&
          members.data &&
          members.data.data.every((row) => targetIdSet.has(row.id)) &&
          search.current > 1
        ) {
          await navigateSearch({
            replace: true,
            search: (previous) => ({ ...previous, current: previous.current - 1 })
          })
        }
      }
    })
  }

  const batchMenu: MenuProps = {
    items: [
      { key: 'enable', label: '批量恢复' },
      { danger: true, key: 'disable', label: '批量停用' },
      ...(currentSession.isSuperAdmin ? [{ key: 'delete', danger: true, label: '批量删除成员' }] : [])
    ],
    onClick: ({ key }) =>
      key === 'delete' ? confirmDeleteMembers(selectedMemberIds) : confirmChangeSelectedStatus(key === 'disable')
  }

  const confirmSetDisabledMember = (member: MemberRow) => {
    if (member.isDisable) {
      void changeMemberStatus(member)
      return
    }
    modal.confirm({
      content: '成员账号也会同时停用。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeMemberStatus(member),
      title: `停用成员 ${member.name}？`
    })
  }

  const canManageMember = (member: MemberRow) => (member.type === 'agent' ? canManageAgents : canManageEmployees)

  return (
    <PageContainer
      breadcrumbRender={false}
      content="成员可以没有账号；启用业务员且未停用的成员可以作为订单业务归属人。"
      title="成员管理"
    >
      <Flex gap="middle" vertical>
        {members.isError ? (
          <Alert
            action={
              <Button onClick={() => void members.refetch()} size="small">
                重试
              </Button>
            }
            showIcon
            title={getErrorMessage(members.error, '成员列表加载失败')}
            type="error"
          />
        ) : null}
        <ProTable<MemberRow, MemberSearch>
          columns={createColumns({
            canManageAgents,
            canManageEmployees,
            isSuperAdmin: currentSession.isSuperAdmin,
            currentMemberId: currentSession.member.id,
            onConfirmDelete: (member) => confirmDeleteMembers([member.id], member),
            onConfirmSetDisabled: confirmSetDisabledMember,
            onEdit: setEditing,
            sortOrder: search.sortOrder
          })}
          columnsState={{ persistenceKey: 'access-members:table', persistenceType: 'localStorage' }}
          dataSource={members.data?.data ?? []}
          headerTitle={`全部成员（${members.data?.total ?? 0}）`}
          loading={{ delay: 150, spinning: members.isFetching }}
          onChange={handleTableChange}
          onReset={table.onReset}
          onSubmit={table.onSubmit}
          options={{ reload: () => void members.refetch() }}
          pagination={{ ...table.pagination, total: members.data?.total ?? 0 }}
          formRef={table.formRef}
          rowKey="id"
          rowSelection={
            canManageEmployees || canManageAgents
              ? {
                  getCheckboxProps: (record) => ({
                    disabled:
                      !canManageMember(record) ||
                      members.isFetching ||
                      batchDeleteMembers.isPending ||
                      deleteMember.isPending ||
                      batchSetDisabled.isPending
                  }),
                  preserveSelectedRowKeys: false,
                  onChange: (keys) => setSelectedMemberIds(keys.map(Number)),
                  selectedRowKeys: selectedMemberIds
                }
              : undefined
          }
          scroll={{ x: '108rem' }}
          form={{ initialValues: table.formInitialValues }}
          search={{ defaultCollapsed: false, labelWidth: 'auto' }}
          size="small"
          toolBarRender={() =>
            canManageEmployees || canManageAgents
              ? [
                  <Dropdown key="batch-actions" menu={batchMenu} placement="bottomRight" trigger={['click']}>
                    <Button
                      disabled={
                        selectedMemberIds.length === 0 ||
                        members.isFetching ||
                        batchDeleteMembers.isPending ||
                        deleteMember.isPending ||
                        batchSetDisabled.isPending
                      }
                      loading={batchDeleteMembers.isPending}
                      icon={<ListChecks aria-hidden size={16} />}
                    >
                      {selectedMemberIds.length ? `批量操作（${selectedMemberIds.length}）` : '批量操作'}{' '}
                      <ChevronDown aria-hidden size={14} />
                    </Button>
                  </Dropdown>,
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建成员
                  </Button>
                ]
              : []
          }
        />
      </Flex>

      <MemberEditorModal
        canManageAgents={canManageAgents}
        canManageEmployees={canManageEmployees}
        editing={editing}
        previewRecord={members.data?.data.find((member) => member.id === editing)}
        onClose={closeEditor}
        onSaved={async () => {
          closeEditor()
          await invalidateMembers()
        }}
      />
    </PageContainer>
  )
}
